import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AnchorContext,
  firstInDocumentOrder,
  type AnchorRegistry,
} from './anchorContext';
import type { AnchorId } from './steps';

type Props = {
  children: ReactNode;
};

export const OnboardingAnchorProvider = ({ children }: Props) => {
  const registry = useRef<AnchorRegistry>(new Map());
  const [version, setVersion] = useState(0);

  const register = useCallback((id: AnchorId, element: HTMLElement | null) => {
    const elements = registry.current.get(id) ?? new Set<HTMLElement>();

    if (element) {
      elements.add(element);
    } else {
      elements.forEach((known) => {
        if (!known.isConnected) elements.delete(known);
      });
    }

    registry.current.set(id, elements);
    setVersion((current) => current + 1);
  }, []);

  const getAnchor = useCallback(
    (id: AnchorId) =>
      firstInDocumentOrder(registry.current.get(id) ?? new Set()),
    []
  );

  const value = useMemo(
    () => ({ register, getAnchor, version }),
    [register, getAnchor, version]
  );

  return (
    <AnchorContext.Provider value={value}>{children}</AnchorContext.Provider>
  );
};
